/**
 * usePermissions Hook
 *
 * Хук для проверки разрешений текущего пользователя.
 */
import { useCallback, useMemo } from 'react';
import { useSession } from '../context/SessionContext';
import { hasPermission as checkPermission, hasAnyPermission as checkAnyPermission, ROLE_PERMISSIONS } from '../constants/permissions';

export function usePermissions() {
  const { user } = useSession();

  const role = user?.role || null;

  const permissions = useMemo(() => {
    if (!role) return [];
    return ROLE_PERMISSIONS[role] || [];
  }, [role]);

  const hasPermission = useCallback((permission) => {
    if (!role) return false;
    return checkPermission(role, permission);
  }, [role]);

  const hasAnyPermission = useCallback((permissionList) => {
    if (!role) return false;
    return checkAnyPermission(role, permissionList);
  }, [role]);

  const hasAllPermissions = useCallback((permissionList) => {
    if (!role) return false;
    return permissionList.every((p) => checkPermission(role, p));
  }, [role]);

  const isAdmin = useMemo(() => role === 'admin', [role]);
  const isManager = useMemo(() => role === 'manager', [role]);
  const isSales = useMemo(() => role === 'sales', [role]);
  const isTeacher = useMemo(() => role === 'teacher', [role]);
  const isViewer = useMemo(() => role === 'viewer', [role]);

  return {
    role,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isManager,
    isSales,
    isTeacher,
    isViewer,
  };
}

export default usePermissions;
